import { Contract, utils, providers, BigNumber } from 'ethers'

import TOKEN_ABI from './tokenAbi'
import MIGRATOR_ABI from './migratorAbi'

export const MAINNET_CHAIN_ID = '0x1'
export const XDAI_CHAIN_ID = '0x64'
export const POLYGON_CHAIN_ID = '0x89'
export const BSC_CHAIN_ID = '0x38'

// mainnet addresses
export const OLD_TOKEN_ADDRESS = '0x0cf0ee63788a0849fe5297f3407f701e122cc023'
export const NEW_TOKEN_ADDRESS = '0x8f693ca8d21b157107184d29d398a8d082b38b76'
export const DATA_FUND_CONTRACT_ADDRESS = '0xf967622d4e753f9429e35127dccdc73713db630b'

// xDai addresses
const XDAI_OLD_TOKEN_ADDRESS = '0xE4a2620edE1058D61BEe5F45F6414314fdf10548'
const XDAI_NEW_TOKEN_ADDRESS = '0x256eb8a51f382650B2A1e946b8811953640ee47D'
const XDAI_MIGRATOR_ADDRESS = '0x29e572d45cC33D5a68DCc8f92bfc7ded0017Bc59'
export const XDAI_PROVIDER = 'https://rpc.gnosischain.com/'

// polygon addresses
const POLYGON_NEW_TOKEN_ADDRESS = '0x3a9A81d576d83FF21f26f325066054540720fC34'

// BSC addresses
const BSC_TOKEN_ADDRESS = '0x0864c156b3c5f69824564dec60c629ae6401bf2a'

export const INFURA_ID = '17c3985baecb4c4d94a1edc2c4d23206'

export const networks = {
    [MAINNET_CHAIN_ID]: {
        name: 'Mainnet',
        currency: 'ETH',
        migratable: true,
        getProvider: () => new providers.InfuraProvider('homestead', {
            projectId: INFURA_ID,
        }),
    },
    [XDAI_CHAIN_ID]: {
        name: 'Gnosis Chain (xDai)',
        currency: 'xDAI',
        migratable: true,
        getConfig: () => ({
            chainName: 'Gnosis Chain (xDai)',
            rpcUrls: [XDAI_PROVIDER],
            blockExplorerUrls: ['https://blockscout.com/xdai/mainnet/'],
            nativeCurrency: {
                name: 'xDAI',
                symbol: 'xDAI',
                decimals: 18,
            },
        }),
        getProvider: () => new providers.JsonRpcProvider(XDAI_PROVIDER),
    },
    [POLYGON_CHAIN_ID]: {
        name: 'Polygon',
        currency: 'MATIC',
        migratable: false,
        getConfig: () => ({
            chainName: 'Polygon Mainnet',
            rpcUrls: ['https://polygon-rpc.com/'],
            blockExplorerUrls: ['https://polygonscan.com/'],
            nativeCurrency: {
                name: 'MATIC',
                symbol: 'MATIC',
                decimals: 18,
            },
        }),
        // TODO: update to private node
        getProvider: () => new providers.JsonRpcProvider('https://polygon-rpc.com/'),
    },
    [BSC_CHAIN_ID]: {
        name: 'Binance Smart Chain',
        currency: 'BNB',
        migratable: false,
        getConfig: () => ({
            chainName: 'Binance Smart Chain Mainnet',
            rpcUrls: ['https://bsc-dataseed1.binance.org/'],
            blockExplorerUrls: ['https://bscscan.com/'],
            nativeCurrency: {
                name: 'BNB',
                symbol: 'BNB',
                decimals: 18,
            },
        }),
        getProvider: () => new providers.JsonRpcProvider('https://bsc-dataseed1.binance.org/'),
    },
}

export function getPublicNodeProvider({ chainId = MAINNET_CHAIN_ID } = {}) {
    const { getProvider } = networks[chainId] || {}

    if (typeof getProvider !== 'function') {
        throw new Error('Provider does not exist!')
    }

    return getProvider()
}

export function getTokens({ chainId = MAINNET_CHAIN_ID } = {}) {
    try {
        const provider = getPublicNodeProvider({
            chainId,
        })

        switch (chainId) {
            case MAINNET_CHAIN_ID: {
                return {
                    // New DATA token contract
                    dataToken: new Contract(NEW_TOKEN_ADDRESS, TOKEN_ABI, provider),
                    // Old DATA token contract (-> XDATA)
                    xdataToken: new Contract(OLD_TOKEN_ADDRESS, TOKEN_ABI, provider),
                }
            }

            case XDAI_CHAIN_ID: {
                return {
                    dataToken: new Contract(XDAI_NEW_TOKEN_ADDRESS, TOKEN_ABI, provider),
                    xdataToken: new Contract(XDAI_OLD_TOKEN_ADDRESS, TOKEN_ABI, provider),
                    migrator: new Contract(XDAI_MIGRATOR_ADDRESS, MIGRATOR_ABI, provider),
                }
            }

            case POLYGON_CHAIN_ID: {
                return {
                    dataToken: new Contract(POLYGON_NEW_TOKEN_ADDRESS, TOKEN_ABI, provider),
                }
            }

            case BSC_CHAIN_ID: {
                return {
                    dataToken: new Contract(BSC_TOKEN_ADDRESS, TOKEN_ABI, provider),
                }
            }

            default:
                break
        }
    } catch (e) {
        console.warn(e)
    }

    return {}
}

export async function getDataFundBalance() {
    const { dataToken } = getTokens()

    const balance = await dataToken.balanceOf(DATA_FUND_CONTRACT_ADDRESS)
    return parseFloat(utils.formatEther(balance, 18))
}

export const toHexString = (number) => (
    utils.hexStripZeros(utils.hexlify(number))
)

export const toNumber = (hexString) => (
    BigNumber.from(hexString).toNumber()
)
